/**
 * Utility functions for Products page
 */

import { Product, Brand, Model } from "../../../types";
import {
  ProductManagementState,
  ProductManagementAction,
} from "../types/products-types";
import { shouldHideDiscount } from "../../../utils/discounts";

/**
 * Initial state for product management
 */
export const getInitialProductManagementState = (): ProductManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  editingProduct: null,
  deletingProduct: null,
});

/**
 * Product management state reducer
 */
export const productManagementReducer = (
  state: ProductManagementState,
  action: ProductManagementAction
): ProductManagementState => {
  switch (action.type) {
    case "OPEN_CREATE_DRAWER":
      return {
        ...state,
        isDrawerOpen: true,
        editingProduct: null,
      };
    case "OPEN_EDIT_DRAWER":
      return {
        ...state,
        isDrawerOpen: true,
        editingProduct: action.payload,
      };
    case "CLOSE_DRAWER":
      return {
        ...state,
        isDrawerOpen: false,
        editingProduct: null,
      };
    case "OPEN_DELETE_MODAL":
      return {
        ...state,
        isDeleteModalOpen: true,
        deletingProduct: action.payload,
      };
    case "CLOSE_DELETE_MODAL":
      return {
        ...state,
        isDeleteModalOpen: false,
        deletingProduct: null,
      };
    default:
      return state;
  }
};

/**
 * Get status badge configuration
 */
export const getStatusBadge = (status: string) => {
  const statusConfig = {
    RENTED: { label: "Арендован", className: "status-rented" },
    ACTIVE: { label: "Активен", className: "status-active" },
    ARCHIVED: { label: "Архивный", className: "status-archived" },
  };

  return (
    statusConfig[status as keyof typeof statusConfig] || {
      label: "Активен",
      className: "status-active",
    }
  );
};

/**
 * Get fuel type label
 */
export const getFuelTypeLabel = (fuelType: string): string => {
  const fuelTypeLabels = {
    GASOLINE: "Бензин",
    DIESEL: "Дизель",
    ELECTRIC: "Электрический",
    HYBRID: "Гибридный",
    LPG: "Газ",
  };
  return fuelTypeLabels[fuelType as keyof typeof fuelTypeLabels] || fuelType;
};

/**
 * Get transmission label
 */
export const getTransmissionLabel = (transmission: string): string => {
  const transmissionLabels = {
    MANUAL: "МКПП",
    AUTOMATIC: "АКПП",
    CVT: "Вариатор",
    SEMI_AUTOMATIC: "РКПП",
  };
  return (
    transmissionLabels[transmission as keyof typeof transmissionLabels] ||
    transmission
  );
};

/**
 * Get rental period label
 */
export const getRentalPeriodLabel = (period: string): string => {
  const rentalPeriodLabels = {
    HOUR: "час",
    DAY: "день",
    WEEK: "неделя",
    MONTH: "месяц",
  };
  return (
    rentalPeriodLabels[period as keyof typeof rentalPeriodLabels] || period
  );
};

/**
 * Get product display name
 */
export const getProductDisplayName = (product: Product): string => {
  return (
    `${product.brand || ""} ${product.model || ""}`.trim() ||
    "Неизвестный автомобиль"
  );
};

/**
 * Get product specifications text
 */
export const getProductSpecsText = (product: Product): string => {
  const fuelType = getFuelTypeLabel(product.fuelType || "GASOLINE");
  const engineSize = product.engineSize || 0;
  const drivetrain = product.drivetrain || "FWD";

  return `${fuelType} • ${engineSize}л • ${drivetrain}`;
};

/**
 * Get rental price display text
 */
export const getRentalPriceText = (product: Product): string => {
  const rental = product.rental || {
    status: "ACTIVE",
    rentalPeriod: "DAY",
    price: 0,
    hasDiscount: false,
    discountType: "HOURS",
    discountDuration: 0,
    discountCreatedAt: null,
  };

  // Use rental price if it's set and greater than 0, otherwise fall back to main product price
  const price =
    rental.price > 0
      ? rental.price
      : product.price
      ? parseFloat(product.price.toString())
      : 0;
  const periodLabel = getRentalPeriodLabel(rental.rentalPeriod);
  return `${price.toLocaleString()} ₽/${periodLabel}`;
};

/**
 * Check if product has an active discount (not expired)
 */
export const hasActiveDiscount = (product: Product): boolean => {
  const rental = product.rental;

  if (!rental?.hasDiscount || !rental.discountDuration) {
    return false;
  }

  // If discountCreatedAt is null, consider the discount as active
  // This handles cases where discount was created but creation date wasn't recorded
  if (!rental.discountCreatedAt) {
    return true;
  }

  // Return true if discount exists and should NOT be hidden (i.e., it's active)
  return !shouldHideDiscount(product);
};

/**
 * Get discount display text
 */
export const getDiscountText = (product: Product): string | null => {
  const rental = product.rental;

  if (!rental?.hasDiscount || !rental.discountDuration) {
    return null;
  }

  // Check if discount is still active
  if (!hasActiveDiscount(product)) {
    return null;
  }

  const discountTypeLabel = rental.discountType === "HOURS" ? "часов" : "дней";
  return `Скидка: ${rental.discountDuration} ${discountTypeLabel}`;
};

/**
 * Get discounted rental price text with actual discount calculation
 */
export const getDiscountedRentalPriceText = (product: Product): string => {
  const rental = product.rental || {
    status: "ACTIVE",
    rentalPeriod: "DAY",
    price: 0,
    hasDiscount: false,
    discountType: "HOURS",
    discountDuration: 0,
    discountCreatedAt: null,
  };

  // Use rental price if it's set and greater than 0, otherwise fall back to main product price
  const originalPrice =
    rental.price > 0
      ? rental.price
      : product.price
      ? parseFloat(product.price.toString())
      : 0;

  // Check if discount is active and should be applied
  if (!rental.hasDiscount || !hasActiveDiscount(product)) {
    const periodLabel = getRentalPeriodLabel(rental.rentalPeriod);
    return `${originalPrice.toLocaleString()} ₽/${periodLabel}`;
  }

  let discountedPrice = originalPrice;

  // Calculate discount based on discount type
  if (rental.discountType === "HOURS" || rental.discountType === "DAYS") {
    // Free periods discount: treat discountDuration as number of free hours/days
    const freePeriods = Math.max(0, rental.discountDuration || 0);

    // Convert rental period to hours for consistent calculation
    let rentalPeriodInHours = 1;
    if (rental.rentalPeriod === "DAY") {
      rentalPeriodInHours = 24;
    } else if (rental.rentalPeriod === "HOUR") {
      rentalPeriodInHours = 1;
    }

    // Convert discount duration to hours
    let discountInHours = freePeriods;
    if (rental.discountType === "DAYS") {
      discountInHours = freePeriods * 24;
    }

    // Calculate proportional discount
    if (discountInHours >= rentalPeriodInHours) {
      // If free periods >= rental period, the price is free
      discountedPrice = 0;
    } else {
      // Calculate proportional price reduction
      discountedPrice =
        originalPrice * (1 - discountInHours / rentalPeriodInHours);
    }
  }

  // Ensure price doesn't go negative
  discountedPrice = Math.max(0, discountedPrice);

  // Handle edge case where calculation results in NaN
  if (isNaN(discountedPrice)) {
    discountedPrice = originalPrice;
  }

  const periodLabel = getRentalPeriodLabel(rental.rentalPeriod);
  return `${Math.round(discountedPrice).toLocaleString()} ₽/${periodLabel}`;
};

/**
 * Check if product is active
 */
export const isProductActive = (product: Product): boolean => {
  return product.isActive !== false;
};

/**
 * Check if product is featured
 */
export const isProductFeatured = (product: Product): boolean => {
  return product.isFeatured === true;
};

/**
 * Get product image alt text
 */
export const getProductImageAlt = (product: Product): string => {
  return getProductDisplayName(product);
};

/**
 * Format mileage for display
 */
export const formatMileage = (mileage: number): string => {
  return mileage.toLocaleString();
};

/**
 * Sort products by order field
 * Products with order 0 come last (since 0 is the default value)
 */
export const sortProductsByOrder = (products: Product[]): Product[] => {
  return [...products].sort((a, b) => {
    const orderA = a.order || 0;
    const orderB = b.order || 0;

    // If both have order 0, maintain original order
    if (orderA === 0 && orderB === 0) {
      return 0;
    }

    // If only one has order 0, it comes last
    if (orderA === 0) return 1;
    if (orderB === 0) return -1;

    // Otherwise, sort by order number
    return orderA - orderB;
  });
};

/**
 * Validate product form data
 */
export const validateProductData = (data: Partial<Product>): string[] => {
  const errors: string[] = [];

  // Check for brandId instead of brand (API expects brandId)
  if (!data.brandId?.trim()) {
    errors.push("Бренд обязателен");
  }

  // Check for modelId instead of model (API expects modelId)
  if (!data.modelId?.trim()) {
    errors.push("Модель обязательна");
  }

  if (
    !data.year ||
    data.year < 1900 ||
    data.year > new Date().getFullYear() + 1
  ) {
    errors.push("Некорректный год");
  }

  if (data.mileage !== undefined && data.mileage < 0) {
    errors.push("Пробег не может быть отрицательным");
  }

  if (
    data.engineSize !== undefined &&
    (data.engineSize < 0.1 || data.engineSize > 10)
  ) {
    errors.push("Объем двигателя должен быть от 0.1 до 10 литров");
  }

  if (data.rental?.price !== undefined && data.rental.price < 0) {
    errors.push("Цена аренды не может быть отрицательной");
  }

  // Check required fields for API
  if (!data.fuelType) {
    errors.push("Тип топлива обязателен");
  }

  if (!data.transmission) {
    errors.push("Коробка передач обязательна");
  }

  if (!data.drivetrain) {
    errors.push("Привод обязателен");
  }

  if (!data.licensePlate?.trim()) {
    errors.push("Госномер обязателен");
  }

  if (!data.rental?.status) {
    errors.push("Статус аренды обязателен");
  }

  if (!data.rental?.rentalPeriod) {
    errors.push("Период аренды обязателен");
  }

  // lotNumber will be auto-generated from licensePlate, so no need to validate it
  // But ensure we have a licensePlate to generate lotNumber from
  if (!data.licensePlate?.trim() && !data.lotNumber?.trim()) {
    errors.push("Госномер обязателен для генерации номера лота");
  }

  return errors;
};

/**
 * Normalize product data for API
 */
export const normalizeProductData = (product: any): Product => {
  return {
    ...product,
    // Handle brand and model - they might be objects from populated API responses
    brand:
      typeof product.brand === "object"
        ? product.brand?.name || product.brand?.title || "—"
        : product.brand || "—",
    model:
      typeof product.model === "object"
        ? product.model?.name || product.model?.title || "—"
        : product.model || "—",
    // Ensure rental object exists with default values
    rental: product.rental || {
      status: "ACTIVE",
      rentalPeriod: "DAY",
      price: 0,
      hasDiscount: false,
      discountType: "HOURS",
      discountDuration: 0,
      discountAmount: 0,
      discountCreatedAt: null,
    },
    // Ensure other optional fields have defaults
    images: product.images || [],
    mainImage: product.mainImage || "",
    meta: product.meta || {
      title: "",
      description: "",
      keywords: "",
    },
    isActive: product.isActive !== undefined ? product.isActive : true,
    isFeatured: product.isFeatured !== undefined ? product.isFeatured : false,
    order: product.order || 0,
  };
};

/**
 * Extract products from API response
 */
export const extractProductsFromResponse = (data: any): Product[] => {
  let productList: any[] = [];

  if (data) {
    // Check for products array directly
    if (Array.isArray(data?.products)) {
      productList = data.products;
    }
    // Check for data.products structure
    else if (Array.isArray(data?.data?.products)) {
      productList = data.data.products;
    }
    // Check if data itself is an array
    else if (Array.isArray(data)) {
      productList = data;
    }
    // Check for data.data array structure
    else if (Array.isArray(data?.data)) {
      productList = data.data;
    }
  }

  // Normalize and sort products by order
  const normalizedProducts = productList.map(normalizeProductData);
  return sortProductsByOrder(normalizedProducts);
};

/**
 * Extract brands from API response
 */
export const extractBrandsFromResponse = (data: any): Brand[] => {
  let brandList: any[] = [];

  if (data) {
    // Check for brands array directly
    if (Array.isArray(data?.brands)) {
      brandList = data.brands;
    }
    // Check for data.brands structure
    else if (Array.isArray(data?.data?.brands)) {
      brandList = data.data.brands;
    }
    // Check if data itself is an array
    else if (Array.isArray(data)) {
      brandList = data;
    }
    // Check for data.data array structure
    else if (Array.isArray(data?.data)) {
      brandList = data.data;
    }
  }

  return brandList.map((b: any) => ({
    id: b._id || b.id,
    name: b.name,
    logo: b.logo,
    description: b.description,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  }));
};

/**
 * Extract models from API response
 */
export const extractModelsFromResponse = (data: any): Model[] => {
  let modelList: any[] = [];

  if (data) {
    // Check for models array directly
    if (Array.isArray(data?.models)) {
      modelList = data.models;
    }
    // Check for data.models structure
    else if (Array.isArray(data?.data?.models)) {
      modelList = data.data.models;
    }
    // Check if data itself is an array
    else if (Array.isArray(data)) {
      modelList = data;
    }
    // Check for data.data array structure
    else if (Array.isArray(data?.data)) {
      modelList = data.data;
    }
  }

  return modelList.map((m: any) => ({
    id: m._id || m.id,
    name: m.name,
    brandId: m.brandId,
    createdAt: m.createdAt,
    updatedAt: m.updatedAt,
  }));
};
